import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './UserInfo.css';
import { useNavigate } from 'react-router-dom';
import Button from '../Button/Button';
import { convertApiRoleToUiRole } from '../../utils/roleUtils';

const getContactUpdateRoute = (role: string) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  switch (role) {
    case 'Acheteur signataire admin':
      return `${apiUrl}/responsables-achat/me`;
    case 'Acheteur signataire':
      return `${apiUrl}/validateurs-achat/me`;
    case 'Acheteur':
      return `${apiUrl}/acheteurs/me`;
    case 'Prestataire signataire admin':
      return `${apiUrl}/gestionnaires/me`;
    case 'Prestataire signataire':
      return `${apiUrl}/signataires/me`;
    case 'Prestataire':
      return `${apiUrl}/interlocuteurs/me`;
    // Format UI (avec espace)
    case 'Prestataire libre admin':
    case 'Prestataire libre':
    // Format API (avec underscore) - CORE-21
    case 'Prestataire_Libre':
    case 'Prestataire_Libre_Admin':
      return `${apiUrl}/prestataires-libres/me`;
    default:
      return '';
  }
};

const MissingInfoPopup = ({ isOpen, userInfo, onSubmit }) => {
  const safeUser = userInfo || {};

  const [form, setForm] = useState({
    nom: safeUser.nom === "Non renseigne" ? "" : (safeUser.nom || ""),
    prenom: safeUser.prenom === "Non renseigne" ? "" : (safeUser.prenom || ""),
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-articles-overlay">
      <div className="modal-articles">
        <div className="modal-articles-header">
          <h2>Complétez vos informations</h2>
        </div>

        <div className="modal-articles-content" style={{ padding: "2rem" }}>
          <p style={{ marginBottom: "2rem" }}>Merci de renseigner les informations manquantes :</p>

          <div className="form-group">
            <label>Nom <span style={{ color: 'red' }}>*</span></label>
            <input
              name="nom"
              value={form.nom}
              onChange={handleChange}
              placeholder="Nom"
              required
            />
          </div>

          <div className="form-group">
            <label>Prénom <span style={{ color: 'red' }}>*</span></label>
            <input
              name="prenom"
              value={form.prenom}
              onChange={handleChange}
              placeholder="Prénom"
              required
            />
          </div>

          <Button
            onClick={() => onSubmit(form)}
            value="Valider"
            disabled={!form.nom.trim() || !form.prenom.trim()}
          />

        </div>
      </div>
    </div>
  );
};

const UserInfo: React.FC = () => {
  const { user, logout } = useAuth();
  const [userInfo, setUserInfo] = useState<{ nom?: string; email_contact?: string; prenom?: string; organisation?: string; role?: string } | null>(null);
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);

  const updateUserInfo = async (updated) => {
    if (!user?.role) return;
    const route = `${process.env.REACT_APP_API_URL}/users/me`;
    if (!route) return;

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(route, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nom: updated.nom,
          prenom: updated.prenom,
        })
      });

      if (response.ok) {
        const data = await response.json();
        setUserInfo({
          ...userInfo,
          nom: data.data[0].nom,
          prenom: data.data[0].prenom,
        });
      }
    } catch (e) {
      setUserInfo({});
    }
  }

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!user?.role) return;
      const route = getContactUpdateRoute(user.role);
      if (!route) return;

      const token = localStorage.getItem('token');

      try {
        const response = await fetch(route, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          logout();
          navigate('/login');
          return;
        }

        if (response.status === 404) {
          const errorData = await response.json();
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          logout();
          navigate('/login', { state: { error: errorData.message || 'Utilisateur non trouvé' } });
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          logout();
          navigate('/login', { state: { error: errorData.message || 'Une erreur est survenue' } });
          return;
        }

        if (response.ok) {
          const data = await response.json();

          const infos = {
            nom: data.data.nom,
            prenom: data.data.prenom,
            organisation: data.data.organisme?.nom ?? data.data.entite?.nom,
            role: user.role,
            email_contact: data.data.email_contact
          };

          setUserInfo(infos);

          const missing =
            !infos.nom || infos.nom === "Non renseigne" ||
            !infos.prenom || infos.prenom === "Non renseigne" ||
            !infos.email_contact || infos.email_contact === "Non renseigne";

          if (missing) {
            setShowPopup(true);
          }
        }
      } catch (e) {
        setUserInfo({});
      }
    };

    fetchUserInfo();
  }, [user?.role, navigate, logout]);

  if (userInfo === null) {
    return (
      <div className="user-info-display">
        <div className="user-info-name">Chargement...</div>
      </div>
    );
  }

  if (Object.keys(userInfo).length === 0) {
    return (
      <div className="user-info-display">
        <div className="user-info-name">Visiteur</div>
      </div>
    );
  }

  return (
    <>
      <div className="user-info-display">
        <div className="user-info-name">
          {userInfo.nom || ''} {userInfo.prenom || ''} ({userInfo.email_contact}), {userInfo.organisation || 'Organisation'}, <span className='user-info-role'>{userInfo.role ? convertApiRoleToUiRole(userInfo.role) : 'Role'}</span>
        </div>
      </div>

      <MissingInfoPopup
        isOpen={showPopup}
        userInfo={userInfo}
        onSubmit={(updated) => {
          // Tu feras ici la requête PUT pour update l'utilisateur.
          updateUserInfo(updated);
          setShowPopup(false);
        }}
      />
    </>
  );
};

export default UserInfo;